import {
    activityCategories,
    ActivityCategory,
    ActivityRecordType,
    CalendarDate,
    CommunityOrgTypeSentinel,
    isActivityRecordType,
    isUuidString,
    maxActivitiesReportStaffFilter,
    NoOrgTypeSentinel,
    ReferralTypesOptions,
    TaskTypeId,
    UserId,
} from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import type { inferRouterOutputs } from "@trpc/server";
import { ActivityRowIconKind } from "components/details-page-v-next/activity-rows";
import { DatePreset, DateRangeValue } from "components/reports/date-presets";
import { ActivityTypeOption } from "components/tasks/activity-type-options";
import { stableQueryOptionsRpc } from "util/requests";

type RouterOutput = inferRouterOutputs<AppRouter>;

export type ActivitiesReportCountMode = "category" | "activityType";

export interface ActivitiesReportCount {
    userId: UserId;
    kind: "activity" | "note";
    category: ActivityCategory | null;
    taskTypeId: TaskTypeId | null;
    taskTypeName: string | null;
    count: number;
}

export type ActivitiesReportColumnFilter =
    | { kind: "all" }
    | { kind: "category"; category: ActivityCategory }
    | { kind: "activityType"; taskTypeId: TaskTypeId }
    | { kind: "notes" }
    | { kind: "untyped" };

export interface ActivityColumn {
    key: string;
    label: string;
    iconKind: ActivityRowIconKind | null;
    hint: string | null;
    columnFilter: ActivitiesReportColumnFilter;
}

interface LeaderboardCell {
    columnKey: string;
    count: number;
}

export interface LeaderboardRow {
    userId: UserId;
    name: string;
    counts: LeaderboardCell[];
    total: number;
}

export interface LeaderboardSort {
    key: string;
    direction: "asc" | "desc";
}

export interface ActivitiesReportFilters {
    range: DateRangeValue;
    staffUserIds: UserId[];
    countMode: ActivitiesReportCountMode;
    recordTypes: ActivityRecordType[];
    organizationTypes: string[];
}

export type ActivitiesReportCountRow = RouterOutput["activitiesReport"]["counts"][number];

export const toActivitiesReportCounts = (
    rows: ActivitiesReportCountRow[],
): ActivitiesReportCount[] =>
    rows.map(row => ({
        userId: row.userId,
        kind: row.kind,
        category: row.category ?? null,
        taskTypeId: row.taskTypeId ?? null,
        taskTypeName: row.taskTypeName ?? null,
        count: row.count,
    }));

export const reportQueryOptions = () => ({ ...stableQueryOptionsRpc(), retry: false });

export const activitiesReportPath = "/reports/show/activities";

export const activitiesReportStaffPath = (staffUserId: UserId): string =>
    `${activitiesReportPath}/staff/${staffUserId}`;

export const activitiesReportRoutePaths = [
    `${activitiesReportPath}/staff/:staffUserId`,
    activitiesReportPath,
];

export const nameSortKey = "name";

export const totalSortKey = "total";

const categoryColumnLabels: Record<ActivityCategory, string> = {
    visit: "Visits",
    call: "Calls",
    sms: "Texts",
    email: "Emails",
    event: "Events",
    other: "Other",
};

const notesColumn: ActivityColumn = {
    key: "notes",
    label: "Notes",
    iconKind: "note",
    hint: "Notes written on a record",
    columnFilter: { kind: "notes" },
};

const untypedColumn: ActivityColumn = {
    key: "untyped",
    label: "No type",
    iconKind: null,
    hint: "Activity logged without an activity type",
    columnFilter: { kind: "untyped" },
};

const unknownActivityTypeLabel = "Unknown activity type";

const referralRecordTypes: ActivityRecordType[] = [
    "referenceContact",
    "referenceBusiness",
];

const everyOrganizationType: string[] = [
    ...ReferralTypesOptions.flatMap(group =>
        group.groupOptions.map(option => option.value),
    ),
    NoOrgTypeSentinel,
    CommunityOrgTypeSentinel,
];

const activityCounts = (counts: ActivitiesReportCount[]): ActivitiesReportCount[] =>
    counts.filter(count => count.kind === "activity");

const categoryIsCounted = (
    counts: ActivitiesReportCount[],
    category: ActivityCategory,
): boolean => activityCounts(counts).some(count => count.category === category);

const eventsApplyTo = (recordTypes: ActivityRecordType[]): boolean =>
    recordTypes.length === 0 ||
    recordTypes.some(recordType => referralRecordTypes.includes(recordType));

const categoryColumnShown = (
    category: ActivityCategory,
    counts: ActivitiesReportCount[],
    recordTypes: ActivityRecordType[],
): boolean => {
    switch (category) {
        case "visit":
        case "call":
        case "sms":
        case "email":
            return true;
        case "event":
            return eventsApplyTo(recordTypes) || categoryIsCounted(counts, category);
        case "other":
            return categoryIsCounted(counts, category);
        default: {
            const exhaustiveCheck: never = category;
            throw new Error(`Unhandled activity category: ${exhaustiveCheck}`);
        }
    }
};

const categoryColumns = (
    counts: ActivitiesReportCount[],
    recordTypes: ActivityRecordType[],
): ActivityColumn[] =>
    activityCategories
        .filter(category => categoryColumnShown(category, counts, recordTypes))
        .map(
            (category): ActivityColumn => ({
                key: `category:${category}`,
                label: categoryColumnLabels[category],
                iconKind: category,
                hint: null,
                columnFilter: { kind: "category", category },
            }),
        );

interface CountedActivityType {
    name: string | null;
    category: ActivityCategory | null;
}

const countedActivityType = (
    counts: ActivitiesReportCount[],
    taskTypeId: TaskTypeId,
): CountedActivityType => {
    const counted = counts.find(count => count.taskTypeId === taskTypeId);
    return {
        name: counted?.taskTypeName ?? null,
        category: counted?.category ?? null,
    };
};

const activityTypeColumn = (
    taskTypeId: TaskTypeId,
    counted: CountedActivityType,
    option: ActivityTypeOption | undefined,
): ActivityColumn => ({
    key: `activityType:${taskTypeId}`,
    label: counted.name ?? option?.name ?? unknownActivityTypeLabel,
    iconKind: option?.category ?? counted.category,
    hint: null,
    columnFilter: { kind: "activityType", taskTypeId },
});

const activityTypeColumns = (
    counts: ActivitiesReportCount[],
    activityTypes: ActivityTypeOption[],
): ActivityColumn[] => {
    const usedIds = new Set(
        activityCounts(counts)
            .map(count => count.taskTypeId)
            .filter((taskTypeId): taskTypeId is TaskTypeId => taskTypeId !== null),
    );
    const knownIds = new Set(activityTypes.map(option => option.id));
    const known = activityTypes
        .filter(option => usedIds.has(option.id))
        .map(option =>
            activityTypeColumn(option.id, countedActivityType(counts, option.id), option),
        );
    const unknown = Array.from(usedIds)
        .filter(taskTypeId => !knownIds.has(taskTypeId))
        .map(taskTypeId =>
            activityTypeColumn(
                taskTypeId,
                countedActivityType(counts, taskTypeId),
                undefined,
            ),
        );
    return [...known, ...unknown];
};

const hasUntypedActivity = (counts: ActivitiesReportCount[]): boolean =>
    activityCounts(counts).some(count => count.taskTypeId === null);

export const activityColumnsFor = (
    mode: ActivitiesReportCountMode,
    counts: ActivitiesReportCount[],
    activityTypes: ActivityTypeOption[],
    recordTypes: ActivityRecordType[],
): ActivityColumn[] => {
    switch (mode) {
        case "category":
            return [...categoryColumns(counts, recordTypes), notesColumn];
        case "activityType":
            return [
                ...activityTypeColumns(counts, activityTypes),
                notesColumn,
                ...(hasUntypedActivity(counts) ? [untypedColumn] : []),
            ];
        default: {
            const exhaustiveCheck: never = mode;
            throw new Error(`Unhandled count mode: ${exhaustiveCheck}`);
        }
    }
};

const countMatchesColumn = (
    count: ActivitiesReportCount,
    column: ActivityColumn,
): boolean => {
    const filter = column.columnFilter;
    const kind = filter.kind;
    switch (kind) {
        case "all":
            return true;
        case "category":
            return count.kind === "activity" && count.category === filter.category;
        case "activityType":
            return count.kind === "activity" && count.taskTypeId === filter.taskTypeId;
        case "notes":
            return count.kind === "note";
        case "untyped":
            return count.kind === "activity" && count.taskTypeId === null;
        default: {
            const exhaustiveCheck: never = kind;
            throw new Error(`Unhandled column filter: ${exhaustiveCheck}`);
        }
    }
};

export const columnTotal = (
    counts: ActivitiesReportCount[],
    column: ActivityColumn,
): number =>
    counts
        .filter(count => countMatchesColumn(count, column))
        .reduce((sum, count) => sum + count.count, 0);

export const overallTotal = (counts: ActivitiesReportCount[]): number =>
    counts.reduce((sum, count) => sum + count.count, 0);

const usersInOrder = (counts: ActivitiesReportCount[]): UserId[] =>
    Array.from(new Set(counts.map(count => count.userId)));

const leaderboardRow = (
    userId: UserId,
    counts: ActivitiesReportCount[],
    columns: ActivityColumn[],
    nameOf: (userId: UserId) => string,
): LeaderboardRow => {
    const mine = counts.filter(count => count.userId === userId);
    return {
        userId,
        name: nameOf(userId),
        counts: columns.map(column => ({
            columnKey: column.key,
            count: columnTotal(mine, column),
        })),
        total: overallTotal(mine),
    };
};

export const leaderboardRows = (
    counts: ActivitiesReportCount[],
    columns: ActivityColumn[],
    staffUserIds: UserId[],
    nameOf: (userId: UserId) => string,
): LeaderboardRow[] => {
    const people = staffUserIds.length > 0 ? staffUserIds : usersInOrder(counts);
    return people
        .map(userId => leaderboardRow(userId, counts, columns, nameOf))
        .filter(row => staffUserIds.length > 0 || row.total > 0);
};

const sortValue = (row: LeaderboardRow, key: string): number =>
    key === totalSortKey
        ? row.total
        : (row.counts.find(cell => cell.columnKey === key)?.count ?? 0);

export const sortLeaderboard = (
    rows: LeaderboardRow[],
    sort: LeaderboardSort,
): LeaderboardRow[] => {
    const direction = sort.direction === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
        if (sort.key === nameSortKey) {
            return direction * a.name.localeCompare(b.name);
        }
        return (
            direction * (sortValue(a, sort.key) - sortValue(b, sort.key)) ||
            a.name.localeCompare(b.name)
        );
    });
};

export const orgTypeFilterApplies = (recordTypes: ActivityRecordType[]): boolean =>
    recordTypes.some(recordType => referralRecordTypes.includes(recordType));

export const withRecordTypes = (
    filters: ActivitiesReportFilters,
    recordTypes: ActivityRecordType[],
): ActivitiesReportFilters => ({
    ...filters,
    recordTypes,
    organizationTypes: orgTypeFilterApplies(recordTypes) ? filters.organizationTypes : [],
});

export const withRecordTypesUsedInOffice = (
    filters: ActivitiesReportFilters,
    recordTypesUsedInOffice: ActivityRecordType[],
): ActivitiesReportFilters =>
    withRecordTypes(
        filters,
        filters.recordTypes.filter(recordType =>
            recordTypesUsedInOffice.includes(recordType),
        ),
    );

export const organizationTypesForQuery = (organizationTypes: string[]): string[] =>
    everyOrganizationType.every(type => organizationTypes.includes(type))
        ? []
        : organizationTypes;

const presetSlug = (label: string): string => label.toLowerCase().replace(/\s+/g, "-");

const presetRange = (preset: DatePreset): DateRangeValue => {
    const range = preset.getRange();
    return { startDate: range.start, endDate: range.end, activePreset: preset.label };
};

const parseDate = (value: string | null): CalendarDate | null =>
    value && CalendarDate.isRawDate(value)
        ? CalendarDate.parse(value).getOrElse(null)
        : null;

const listParam = (params: URLSearchParams, name: string): string[] =>
    (params.get(name) ?? "").split(",").filter(value => value.length > 0);

const readRange = (
    params: URLSearchParams,
    presets: DatePreset[],
    defaultPreset: DatePreset,
): DateRangeValue => {
    const preset = presets.find(
        candidate => presetSlug(candidate.label) === params.get("preset"),
    );
    if (preset) {
        return presetRange(preset);
    }
    const startDate = parseDate(params.get("start"));
    const endDate = parseDate(params.get("end"));
    if (startDate && endDate) {
        return { startDate, endDate, activePreset: null };
    }
    return presetRange(defaultPreset);
};

const isUserId = (value: string): value is UserId => isUuidString(value);

export const readActivitiesReportFilters = (
    search: string,
    presets: DatePreset[],
    defaultPreset: DatePreset,
): ActivitiesReportFilters => {
    const params = new URLSearchParams(search);
    const recordTypes = listParam(params, "records").filter(isActivityRecordType);
    return {
        range: readRange(params, presets, defaultPreset),
        staffUserIds: listParam(params, "staff")
            .filter(isUserId)
            .slice(0, maxActivitiesReportStaffFilter),
        countMode: params.get("count") === "type" ? "activityType" : "category",
        recordTypes,
        organizationTypes: orgTypeFilterApplies(recordTypes)
            ? listParam(params, "orgtypes").filter(type =>
                  everyOrganizationType.includes(type),
              )
            : [],
    };
};

const setList = (params: URLSearchParams, name: string, values: string[]): void => {
    if (values.length > 0) {
        params.set(name, values.join(","));
    }
};

export const activitiesReportSearch = (
    filters: ActivitiesReportFilters,
    defaultPreset: DatePreset,
): string => {
    const params = new URLSearchParams();
    const activePreset = filters.range.activePreset;
    if (activePreset === null) {
        params.set("start", filters.range.startDate.toString());
        params.set("end", filters.range.endDate.toString());
    } else if (activePreset !== defaultPreset.label) {
        params.set("preset", presetSlug(activePreset));
    }
    setList(params, "staff", filters.staffUserIds);
    if (filters.countMode === "activityType") {
        params.set("count", "type");
    }
    setList(params, "records", filters.recordTypes);
    setList(params, "orgtypes", filters.organizationTypes);
    const query = params.toString();
    return query ? `?${query}` : "";
};
